import React, { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ReferralCodeField } from '@pages/IntroducingBroker/Dashboard/ReferralLink/ReferralCodeField'
import { VARIANT } from '@pages/IntroducingBroker/Dashboard/ReferralLink/ReferralLinkUrlField'
import { ReferralLinkUrlFields } from '@pages/IntroducingBroker/LinkHub/Links/components/ReferralLinkUrlFields'
import { SectionHeader } from '@pages/IntroducingBroker/LinkHub/Links/components/SectionHeader'
import { getLandingPageUrl, getLinkStyles } from '@pages/IntroducingBroker/LinkHub/Links/helpers'
import { useLinkHub } from '@pages/IntroducingBroker/LinkHub/Links/hooks/useLinkHub'
import { LinkProps } from '@pages/IntroducingBroker/LinkHub/Links/types'

import { useWindowResize } from '@utils/domUtils'

import { IntroducingBrokerReferralTypes } from '@model/IntroducingBrokerDetailsDto'

import { Box } from '@shared/components/Box'

import styles from '../../Dashboard/ReferralLink/ReferralLink.module.scss'

export const RegistrationLink = forwardRef<HTMLDivElement | null, LinkProps>(
  ({ ibCode, mtCode, ibLink, mtLink, fetchShortenedIBLink }, ref) => {
    const { ibCodes, links, shortening, landingPages } = useLinkHub({
      ibCode,
      mtCode,
      ibLink,
      mtLink,
      fetchShortenedIBLink,
    })

    const { mainReferralLink, mtReferralLink } = links
    const {
      shortenedMainReferralLink,
      shortenedMTReferralLink,
      isShortMainLinkLoading,
      isShortMTLinkLoading,
      isShorteningAvailable,
      handleIBLinkShortening,
    } = shortening
    const { landingPageUrl, isCustomLpUrlSelected, customLandingPageUrl } = landingPages

    const { t } = useTranslation()
    const isMobile = useWindowResize()

    const [ibCodeId, setIbCodeId] = useState<IntroducingBrokerReferralTypes>(
      IntroducingBrokerReferralTypes.Default
    )

    const resolvedLandingPageUrl = getLandingPageUrl({
      isCustomLpUrlSelected,
      customLandingPageUrl,
      id: landingPageUrl?.id,
    })

    const linkStyles = getLinkStyles(isMobile)
    const hasMultipleCodes = !!ibCode && !!mtCode
    const hasSingleCode = !!ibCode || !!mtCode

    const activeReferralType = hasMultipleCodes
      ? ibCodeId
      : ibCode
      ? IntroducingBrokerReferralTypes.Default
      : IntroducingBrokerReferralTypes.MultiTier

    return (
      <Box flex direction='column' fullWidth ref={ref}>
        <SectionHeader
          title={t('IB.LinkHubPage.Referral link for registration')}
          activeTab={ibCodeId}
          setActiveTab={setIbCodeId}
          shouldRenderSegmentedControl={hasMultipleCodes}
        />
        <ReferralLinkUrlFields
          activeReferralType={activeReferralType}
          mainReferralLink={mainReferralLink}
          mtReferralLink={mtReferralLink}
          shortenedMainReferralLink={shortenedMainReferralLink}
          shortenedMTReferralLink={shortenedMTReferralLink}
          isShortMainLinkLoading={isShortMainLinkLoading}
          isShortMTLinkLoading={isShortMTLinkLoading}
          isShorteningAvailable={isShorteningAvailable}
          handleIBLinkShortening={handleIBLinkShortening}
          isCustomLpUrlSelected={isCustomLpUrlSelected}
          landingPageUrl={landingPageUrl}
          resolvedLandingPageUrl={resolvedLandingPageUrl}
          ibCode={ibCode}
          mtCode={mtCode}
          mtLink={mtLink}
          variant={VARIANT.NO_LABEL}
          mainLinkMt={hasSingleCode ? linkStyles.mt : 'x8'}
        />
        <Box mt={linkStyles.mt}>
          <ul className={styles.iBReferralLinkList}>
            {ibCodes
              .filter((code) => code.referralType === activeReferralType)
              .map((item) => (
                <ReferralCodeField key={item.value} item={item} value={item.value} isLinkHub />
              ))}
          </ul>
        </Box>
      </Box>
    )
  }
)
